import { cn } from '@/lib/cn';
import { StatusControl } from '@/components/StatusControl';
import { useSearchParams } from 'react-router-dom';
import { useCanEdit } from '@/auth/account';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FolderPlus, Layers, Pencil, Plus } from 'lucide-react';
import type { Group, Investment } from '@/api/types';
import { useGroups, useInvestments, useReorderGroups, useSummary } from '@/api/hooks';
import { formatMonthRange, formatMoney } from '@/lib/format';
import { UNGROUPED_COLOR } from '@/lib/palette';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SortableList, SortableRow } from '@/components/SortableList';
import { GroupDialog } from '@/components/GroupDialog';
import { InvestmentDialog } from '@/components/InvestmentDialog';
import { PageHeader, QueryState } from '@/components/PageHeader';

function InvestmentRow({
  investment,
  currency,
  onOpen,
}: {
  investment: Investment;
  currency: string;
  onOpen: () => void;
}) {
  // The row opens the dialog; the status control sits outside the button (no nested controls).
  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-[var(--surface-2)]">
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <p className="truncate text-sm font-medium">{investment.title}</p>
        <p className="mt-0.5 text-xs text-[var(--ink-3)]">
          {formatMonthRange(investment.plannedFrom, investment.plannedTo)}
          {investment.positionCount > 0
            ? ` · ${investment.positionCount} Position${investment.positionCount === 1 ? '' : 'en'}`
            : ''}
        </p>
      </button>
      <div className="flex shrink-0 items-center gap-3">
        <StatusControl investment={investment} />
        <span onClick={onOpen} className="num w-24 cursor-pointer text-right text-sm font-medium">
          {formatMoney(investment.effectiveCost, currency)}
        </span>
      </div>
    </div>
  );
}

function GroupCard({
  group,
  investments,
  currency,
  onEdit,
  onAdd,
  onOpenInvestment,
  highlighted,
}: {
  group: Group;
  investments: Investment[];
  currency: string;
  onEdit: () => void;
  onAdd: () => void;
  onOpenInvestment: (id: string) => void;
  /** Coming from the dashboard: expand and briefly highlight this group. */
  highlighted?: boolean;
}) {
  const canEdit = useCanEdit();
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (highlighted) setOpen(true);
  }, [highlighted]);

  return (
    <Card id={`gruppe-${group.id}`} className={cn('overflow-hidden scroll-mt-6 transition-shadow duration-700', highlighted && 'ring-2 ring-[var(--accent)]')}>
      <div className="flex items-start gap-3 px-4 py-4">
        <span
          aria-hidden="true"
          className="mt-1 h-9 w-1.5 shrink-0 rounded-full"
          style={{ background: group.color }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold tracking-tight">{group.name}</h2>
            <span className="num text-base font-semibold">
              {formatMoney(group.totalCost, currency)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-[var(--ink-3)]">
            {group.investmentCount} Investition{group.investmentCount === 1 ? '' : 'en'}
            {group.description ? ` · ${group.description}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canEdit ? (
          <Button variant="ghost" size="icon" aria-label={`${group.name} bearbeiten`} onClick={onEdit}>
            <Pencil size={15} />
          </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            aria-label={`${group.name} ${open ? 'einklappen' : 'ausklappen'}`}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <ChevronDown
              size={16}
              className={`transition-transform ${open ? '' : '-rotate-90'}`}
            />
          </Button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-[var(--border)] px-1.5 py-1.5">
          {investments.map((investment) => (
            <InvestmentRow
              key={investment.id}
              investment={investment}
              currency={currency}
              onOpen={() => onOpenInvestment(investment.id)}
            />
          ))}
          {investments.length === 0 ? (
            <p className="px-3 py-3 text-xs text-[var(--ink-3)]">
              Noch keine Investitionen in dieser Gruppe.
            </p>
          ) : null}
          {canEdit ? (
          <div className="px-1.5 pt-1 pb-1.5">
            <Button size="sm" variant="ghost" onClick={onAdd}>
              <Plus size={14} />
              Investition hinzufügen
            </Button>
          </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function Projects() {
  const canEdit = useCanEdit();
  const groups = useGroups();
  const investments = useInvestments();
  const summary = useSummary();
  const reorderGroups = useReorderGroups();

  const [groupDialog, setGroupDialog] = useState<{ open: boolean; group: Group | null }>({
    open: false,
    group: null,
  });
  const [investmentDialog, setInvestmentDialog] = useState<{
    open: boolean;
    id: string | null;
    groupId: string | null;
  }>({ open: false, id: null, groupId: null });
  const [order, setOrder] = useState<string[] | null>(null);

  // Deep link from the dashboard: /projekte?gruppe=<id|ungrouped> scrolls to the group and highlights it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlight, setHighlight] = useState<string | null>(null);
  const target = searchParams.get('gruppe');
  const loaded = groups.data !== undefined && investments.data !== undefined;
  useEffect(() => {
    if (!target || !loaded) return;
    setHighlight(target);
    requestAnimationFrame(() =>
      document.getElementById(`gruppe-${target}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
    );
    setSearchParams({}, { replace: true });
    const timer = setTimeout(() => setHighlight(null), 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, loaded]);

  const currency = summary.data?.currency ?? 'EUR';
  const groupList = groups.data ?? [];

  const ordered = useMemo(() => {
    if (order === null) return groupList;
    return order
      .map((id) => groupList.find((group) => group.id === id))
      .filter((group): group is Group => group !== undefined);
  }, [order, groupList]);

  const byGroup = useMemo(() => {
    const map = new Map<string, Investment[]>();
    for (const investment of investments.data ?? []) {
      const key = investment.groupId ?? '__ungrouped__';
      map.set(key, [...(map.get(key) ?? []), investment]);
    }
    return map;
  }, [investments.data]);

  const ungrouped = byGroup.get('__ungrouped__') ?? [];
  const ungroupedTotal = ungrouped
    .filter((investment) => investment.status !== 'cancelled')
    .reduce((sum, investment) => sum + investment.effectiveCost, 0);

  const handleReorder = (ids: string[]) => {
    setOrder(ids);
    reorderGroups.mutate(ids, { onSettled: () => setOrder(null) });
  };

  return (
    <>
      <PageHeader
        title="Projekte"
        subtitle="Gruppen wie „Terrasse“ bündeln zusammengehörige Investitionen."
        actions={
          !canEdit ? undefined : (
          <>
            <Button onClick={() => setGroupDialog({ open: true, group: null })}>
              <FolderPlus size={15} />
              Neue Gruppe
            </Button>
            <Button
              variant="primary"
              onClick={() => setInvestmentDialog({ open: true, id: null, groupId: null })}
            >
              <Plus size={15} />
              Neue Investition
            </Button>
          </>
          )
        }
      />

      <QueryState
        isLoading={groups.isLoading || investments.isLoading}
        error={((groups.error ?? investments.error) as Error | null) ?? null}
      >
        {ordered.length === 0 && ungrouped.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Layers size={24} />}
              title="Noch nichts geplant"
              description="Lege eine Gruppe wie „Terrasse“ an und hänge Investitionen wie „Boden“ oder „Überdachung“ darunter."
              action={
                !canEdit ? undefined : (
                <Button variant="primary" onClick={() => setGroupDialog({ open: true, group: null })}>
                  <FolderPlus size={15} />
                  Erste Gruppe anlegen
                </Button>
                )
              }
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            <SortableList items={ordered} onReorder={handleReorder} className="gap-4">
              {(group) => (
                <SortableRow key={group.id} id={group.id} handleLabel={group.name}>
                  <GroupCard
                    group={group}
                    currency={currency}
                    investments={byGroup.get(group.id) ?? []}
                    onEdit={() => setGroupDialog({ open: true, group })}
                    onAdd={() =>
                      setInvestmentDialog({ open: true, id: null, groupId: group.id })
                    }
                    onOpenInvestment={(id) =>
                      setInvestmentDialog({ open: true, id, groupId: null })
                    }
                    highlighted={highlight === group.id}
                  />
                </SortableRow>
              )}
            </SortableList>

            {ungrouped.length > 0 ? (
              <Card
                id="gruppe-ungrouped"
                className={cn(
                  'scroll-mt-6 overflow-hidden transition-shadow duration-700',
                  canEdit && 'ml-0 sm:ml-[1.75rem]',
                  highlight === 'ungrouped' && 'ring-2 ring-[var(--accent)]',
                )}
              >
                <div className="flex items-start gap-3 px-4 py-4">
                  <span
                    aria-hidden="true"
                    className="mt-1 h-9 w-1.5 shrink-0 rounded-full"
                    style={{ background: UNGROUPED_COLOR }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h2 className="text-base font-semibold tracking-tight">Ohne Gruppe</h2>
                      <span className="num text-base font-semibold">
                        {formatMoney(ungroupedTotal, currency)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--ink-3)]">
                      Eigenständige Vorhaben, z. B. „Küche“
                    </p>
                  </div>
                </div>
                <div className="border-t border-[var(--border)] px-1.5 py-1.5">
                  {ungrouped.map((investment) => (
                    <InvestmentRow
                      key={investment.id}
                      investment={investment}
                      currency={currency}
                      onOpen={() =>
                        setInvestmentDialog({ open: true, id: investment.id, groupId: null })
                      }
                    />
                  ))}
                </div>
              </Card>
            ) : null}
          </div>
        )}
      </QueryState>

      <GroupDialog
        open={groupDialog.open}
        group={groupDialog.group}
        existingColors={groupList.map((group) => group.color)}
        onClose={() => setGroupDialog({ open: false, group: null })}
      />
      <InvestmentDialog
        open={investmentDialog.open}
        investmentId={investmentDialog.id}
        defaultGroupId={investmentDialog.groupId}
        groups={groupList}
        currency={currency}
        onClose={() => setInvestmentDialog({ open: false, id: null, groupId: null })}
      />

      {groupList.length > 0 && canEdit ? (
        <p className="mt-4 text-xs text-[var(--ink-3)]">
          Tipp: Gruppen lassen sich am Griff links anfassen und neu sortieren.
        </p>
      ) : null}
    </>
  );
}
